import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Pricing: React.FC = () => {
  const plans = [
    {
      name: 'Starter',
      price: 'RM 499',
      period: '/month',
      description: 'Perfect for small businesses and startups',
      features: [
        'Up to 5 active campaigns',
        '50 KOL matches per month',
        'Basic analytics dashboard',
        'Email support',
        'Instagram & TikTok integration',
        'Standard contract templates',
      ],
      notIncluded: [
        'AI script generation',
        'Priority support',
        'Custom integrations',
      ],
      popular: false,
    },
    {
      name: 'Professional',
      price: 'RM 1,499',
      period: '/month',
      description: 'For growing brands ready to scale',
      features: [
        'Unlimited active campaigns',
        '200 KOL matches per month',
        'Advanced analytics & reporting',
        'Priority email & chat support',
        'Instagram, TikTok & YouTube',
        'Custom contract templates',
        'AI script generation (50/month)',
        'Campaign performance alerts',
        'Team collaboration (up to 5)',
      ],
      notIncluded: [
        'Custom integrations',
      ],
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For agencies and large organizations',
      features: [
        'Everything in Professional',
        'Unlimited KOL matches',
        'White-label options',
        'Dedicated account manager',
        '24/7 priority support',
        'Custom AI script generation',
        'Advanced team collaboration',
        'API access',
        'Custom integrations',
        'On-site training',
        'Quarterly strategy reviews',
      ],
      notIncluded: [],
      popular: false,
    },
  ];

  const addOns = [
    {
      name: 'Additional KOL Matches',
      price: 'RM 0.50',
      per: 'per match',
      description: 'Extra AI-powered KOL matches beyond your plan limit',
    },
    {
      name: 'AI Script Credits',
      price: 'RM 0.20',
      per: 'per script',
      description: 'Additional AI-generated content scripts',
    },
    {
      name: 'Team Members',
      price: 'RM 99',
      per: '/user/month',
      description: 'Additional team member seats',
    },
  ];

  const faqs = [
    {
      question: 'Can I change plans anytime?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate your billing accordingly.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express), bank transfers, and online payment platforms like PayPal and Touch \'n Go.',
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes! We offer a 14-day free trial on all plans. No credit card required to start. You get full access to all features during the trial.',
    },
    {
      question: 'What happens if I exceed my KOL match limit?',
      answer: 'You can purchase additional matches at a per-match rate, or upgrade to a higher plan with more matches. We\'ll notify you when you\'re at 80% capacity.',
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes! Annual billing saves you 20% compared to monthly. Contact our sales team for custom enterprise agreements.',
    },
  ];

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="relative px-6 py-24 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Choose the plan that fits your business. Start with a 14-day free trial,
            no credit card required.
          </p>
        </motion.div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative p-8 rounded-2xl border ${
                  plan.popular
                    ? 'bg-gradient-to-br from-violet-900/50 to-blue-900/50 border-violet-500/50'
                    : 'bg-gray-900 border-gray-800'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="px-4 py-1 bg-gradient-to-r from-violet-600 to-blue-600 rounded-full text-sm font-semibold flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm">{plan.description}</p>
                </div>

                <div className="text-center mb-8">
                  <div className="text-5xl font-bold mb-2">
                    {plan.price}
                    <span className="text-xl text-gray-400 font-normal">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 opacity-40">
                      <Check className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-500 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.price === 'Custom' ? '/contact' : '/login'}
                  className={`block w-full rounded-lg py-3 text-center font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-violet-600 to-blue-600 hover:opacity-90'
                      : 'border border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {plan.price === 'Custom' ? 'Contact Sales' : 'Start Free Trial'}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={3} className="px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Optional Add-ons</h2>
            <p className="text-gray-400">
              Customize your plan with additional features and credits
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {addOns.map((addon, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl bg-gray-800/50 border border-gray-800 hover:border-gray-700 transition-colors"
              >
                <h3 className="text-lg font-bold mb-2">{addon.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{addon.description}</p>
                <div className="text-2xl font-bold text-violet-400">
                  {addon.price}
                  <span className="text-sm text-gray-500 font-normal"> {addon.per}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={4} className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Compare Plans</h2>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-4 px-6 font-bold">Feature</th>
                  <th className="text-center py-4 px-6 font-bold">Starter</th>
                  <th className="text-center py-4 px-6 font-bold bg-violet-900/20">Professional</th>
                  <th className="text-center py-4 px-6 font-bold">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Active Campaigns', '5', 'Unlimited', 'Unlimited'],
                  ['KOL Matches', '50/mo', '200/mo', 'Unlimited'],
                  ['AI Script Generation', '❌', '50/mo', 'Unlimited'],
                  ['Analytics', 'Basic', 'Advanced', 'Advanced + Custom'],
                  ['Support', 'Email', 'Priority', '24/7 Dedicated'],
                  ['Team Members', '1', '5', 'Unlimited'],
                  ['API Access', '❌', '❌', '✅'],
                ].map((row, index) => (
                  <tr key={index} className="border-b border-gray-800/50">
                    <td className="py-4 px-6 text-gray-300">{row[0]}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row[1]}</td>
                    <td className="py-4 px-6 text-center text-gray-300 bg-violet-900/10">{row[2]}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={5} className="px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="p-6 rounded-xl bg-gray-800/50 border border-gray-800"
              >
                <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
                <p className="text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={6} className="px-6 py-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-violet-600/20 to-blue-600/20 rounded-3xl p-12 border border-violet-600/30"
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-gray-400 text-lg mb-8">
              Start your 14-day free trial today. No credit card required.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/login"
                className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-8 py-4 text-lg font-semibold transition-opacity hover:opacity-90"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="rounded-lg border border-white/10 px-8 py-4 text-lg font-semibold transition-colors hover:bg-white/10"
              >
                Talk to Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Pricing;
